import { useQuery } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface SerializedUnit {
  id: string;
  serialNumber: string;
  partNumber: string;
  bomId: string;
  lineItemId: string;
  status: 'in_progress' | 'complete' | 'scrapped';
  createdAt: string;
  completedAt: string | null;
  updatedAt: string;
}

export interface AsBuiltComponent {
  id: string;
  serializedUnitId: string;
  partNumber: string;
  quantity: number;
  lotNumber: string | null;
  childSerialNumber: string | null;
  consumedAt: string;
  createdAt: string;
}

export interface LaborEntry {
  id: string;
  taskId: string;
  personId: string;
  personName: string;
  laborBucket: string;
  durationMinutes: number;
  startTime: string;
  endTime: string;
}

export interface TestResult {
  id: string;
  serializedUnitId: string;
  testName: string;
  result: 'pass' | 'fail' | 'pending';
  testerId: string;
  testerName?: string;
  notes: string | null;
  attachmentR2Key: string | null;
  testedAt: string;
  createdAt: string;
}

export interface SerializedUnitDetail extends SerializedUnit {
  components: AsBuiltComponent[];
  labor: LaborEntry[];
  tests: TestResult[];
  // Additional context
  customerName?: string;
  gpSalesOrderNumber?: string;
  lineItemDescription?: string;
}

export interface SerializedUnitListItem {
  id: string;
  serialNumber: string;
  partNumber: string;
  status: 'in_progress' | 'complete' | 'scrapped';
  createdAt: string;
  completedAt: string | null;
  gpSalesOrderNumber?: string;
  customerName?: string;
}

interface SearchParams {
  search?: string;
  status?: string;
  partNumber?: string;
  limit?: number;
  offset?: number;
}

async function searchSerializedUnits(params: SearchParams): Promise<{
  data: SerializedUnitListItem[];
  total: number;
}> {
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.partNumber) searchParams.set('partNumber', params.partNumber);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const response = await fetch(apiUrl(`/api/v1/serialized-units?${searchParams}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to search serialized units');
  }

  const json = (await response.json()) as { data?: SerializedUnitListItem[]; pagination?: { total: number } };
  return {
    data: json.data ?? [],
    total: json.pagination?.total ?? 0,
  };
}

async function fetchSerializedUnit(serialNumber: string): Promise<SerializedUnitDetail> {
  const response = await fetch(apiUrl(`/api/v1/serialized-units/${encodeURIComponent(serialNumber)}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Serialized unit not found');
    }
    throw new Error('Failed to fetch serialized unit');
  }

  const json = (await response.json()) as { data?: SerializedUnitDetail };
  if (!json.data) {
    throw new Error('Invalid response format');
  }
  return json.data;
}

export function useSerializedUnits(params: SearchParams = {}) {
  return useQuery({
    queryKey: ['serializedUnits', params],
    queryFn: () => searchSerializedUnits(params),
    staleTime: 30000,
  });
}

export function useSerializedUnit(serialNumber: string | undefined) {
  return useQuery({
    queryKey: ['serializedUnit', serialNumber],
    queryFn: () => fetchSerializedUnit(serialNumber!),
    enabled: !!serialNumber,
    staleTime: 30000,
  });
}

// Export as-built data as JSON
export function exportAsBuiltJSON(unit: SerializedUnitDetail): string {
  const exportData = {
    serialNumber: unit.serialNumber,
    partNumber: unit.partNumber,
    status: unit.status,
    createdAt: unit.createdAt,
    completedAt: unit.completedAt,
    salesOrder: unit.gpSalesOrderNumber,
    customer: unit.customerName,
    components: unit.components.map((c) => ({
      partNumber: c.partNumber,
      quantity: c.quantity,
      lotNumber: c.lotNumber,
      childSerialNumber: c.childSerialNumber,
      consumedAt: c.consumedAt,
    })),
    labor: unit.labor.map((l) => ({
      laborBucket: l.laborBucket,
      worker: l.personName,
      durationMinutes: l.durationMinutes,
      startTime: l.startTime,
      endTime: l.endTime,
    })),
    tests: unit.tests.map((t) => ({
      testName: t.testName,
      result: t.result,
      testedAt: t.testedAt,
      notes: t.notes,
    })),
    exportedAt: new Date().toISOString(),
  };

  return JSON.stringify(exportData, null, 2);
}
